import '../App.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Helmet } from "react-helmet-async";

type TeamMemberSlot = {
  id: string;
  name: string;
  bio: string;
  /** File in `public/` → value like `/Janet_Bromley.jpg` (leading slash). Use '' in data for placeholder. */
  imageSrc?: string;
  /** Describes the photo for screen readers */
  imageAlt: string;
};

/**
 * All team copy lives here — one object per card (page shows them in pairs, eight total).
 *
 * 1. Add images to the `public` folder at the project root (same place as `favicon.ico`).
 * 2. Set `imageSrc` to `/your-file-name.jpg` (leading slash, no `public` in the path).
 * 3. Set `name` (shown as the heading) and `bio` (paragraph under it).
 * 4. Set `imageAlt` to something like “Portrait of Jane Smith”.
 */
const TEAM_MEMBER_SLOTS: TeamMemberSlot[] = [
  {
    id: '1',
    name: 'Janet Bromley – Home Manager',
    bio: 'Janet has been Registered Manager at Harpwood House for over three years and brings a wealth of experience from not only her nursing background but also many years working in elderly care. She is passionate about getting to know each resident as an individual, taking the time to listen and learn from them, and supporting their families with warmth and honesty. Janet also enjoys working closely with her team, sharing her knowledge and learning from others to ensure the best possible care. She takes great pride in creating a safe, welcoming home where residents feel happy and families feel reassured.',
    imageSrc: '/Janet_Bromley.jpg',
    imageAlt: 'Portrait of Janet Bromley, Home Manager at Harpwood House',
  },
  { id: '2', 
    name: 'Louise Frampton - New Resident Manager', 
    bio: "With around seven years at Harpwood House and over 17 years' experience with the Graham Care Group, Louise plays a key role in welcoming new residents and supporting families from their very first enquiry. She understands that choosing a care home can feel overwhelming and is dedicated to making the process as smooth and reassuring as possible. From initial visits through to settling in, she provides guidance on everything from individual needs to funding options, building strong, supportive relationships along the way so families feel confident and well supported.", 
    imageSrc: '/Louise_Frampton.jpg', 
    imageAlt: 'Portrait of Louise Frampton, New Resident Manager at Harpwood House',
   },
  { id: '3', 
    name: 'Mary Gali - Service Manager', 
    bio: 'Mary brings over 14 years of experience across a wide range of care settings, giving her a broad and valuable understanding of different homes and the needs of those living in them. As Service Manager, she focuses on supporting both residents and their families, ensuring clear communication, strong relationships, and consistently high standards of care. With a warm and compassionate approach, Mary is dedicated to creating a safe, person-centred environment where people feel respected, listened to, and truly at home.', 
    imageSrc: '/Mary_Gali.jpg', 
    imageAlt: 'Portrait of Mary Gali, Service Manager at Harpwood House',
   },
];

function MeetTheTeam() {
  return (
    <>
    <Helmet>
        <title> Meet the Team at Harpwood House </title>
        <meta
          name="description"
          content="Meet the team at Harpwood House. From care and nursing staff to hospitality and management, each member of our team is committed to creating a safe, supportive and truly homely environment for our residents."
        />
        <link rel="canonical" href="https://www.harpwood.house/meet-the-team"/>
      </Helmet>

    <div className="page">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header />

      <main id="main-content">
        <section className="content-section meet-the-team-page">
          <div className="meet-the-team-container">
            <h1>Meet the Team</h1>
            <p className="meet-the-team-intro">
              At Harpwood House, we are proud to have a dedicated and compassionate team at the heart of everything we do. From care and nursing staff to hospitality and management, each member of our team is committed to creating a safe, supportive and truly homely environment for our residents. Get to know the people who make our community so special.
            </p>
          </div>
        </section>

        <section className="content-section meet-the-team-members" aria-label="Team members">
          <div className="meet-the-team-grid">
            {TEAM_MEMBER_SLOTS.map((member) => (
              <article key={member.id} className="meet-the-team-card">
                <div className="meet-the-team-card__media">
                  {member.imageSrc ? (
                    <img
                      src={member.imageSrc}
                      alt={member.imageAlt}
                      className="meet-the-team-card__img"
                    />
                  ) : (
                    <div
                      className="meet-the-team-card__placeholder"
                      role="img"
                      aria-label={member.imageAlt}
                    >
                      <span aria-hidden="true">Photo</span>
                    </div>
                  )}
                </div>
                <h2 className="meet-the-team-card__name">{member.name}</h2>
                <p className="meet-the-team-card__bio">{member.bio}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
    </>
  );
}

export default MeetTheTeam;
